import { action$, reducer, reduceState } from '@ardoq/rxbeach';
import { debounceTime, map, shareReplay } from 'rxjs/operators';
import {
  addToStyleSheet,
  addStylesToStyleSheet,
  EntityCssUpdates,
  clearStyles,
} from './actions';
import { FILTER_STYLES_KEY } from './consts';
import { CssEntry, entityCssOperations } from '@ardoq/scope-data';

type ModelCssStreamShape = {
  styles: Map<string, CssEntry[]>;
};

const handleAddToStyleSheet = (
  state: ModelCssStreamShape,
  entries: EntityCssUpdates
) => {
  for (const [key, css] of entries) {
    if (css && css.length > 0) {
      state.styles.set(key, css);
    } else {
      state.styles.delete(key);
    }
  }
  return state;
};

const handleAddStylesToStyleSheet = (
  state: ModelCssStreamShape,
  stylesArray: EntityCssUpdates[]
) => {
  for (const entries of stylesArray) {
    handleAddToStyleSheet(state, entries);
  }
  return state;
};

const handleClearStyles = (state: ModelCssStreamShape, keys: string[]) => {
  keys.forEach(key => {
    state.styles.delete(key);
  });
  return state;
};

/** A stream providing an array of css classes for Components, Workspace Metamodels, and Conditional Formatting Filters. */
const modelCss$ = action$.pipe(
  reduceState<ModelCssStreamShape>(
    'modelCssProcessing$',
    { styles: new Map() },
    [
      reducer(addToStyleSheet, handleAddToStyleSheet),
      reducer(addStylesToStyleSheet, handleAddStylesToStyleSheet),
      reducer(clearStyles, handleClearStyles),
    ]
  ),
  debounceTime(50),
  map(state =>
    entityCssOperations.orderEntriesByCascade(state.styles, FILTER_STYLES_KEY)
  ),
  // Multicast so subscribers (DepMap, treemap, swimlane viewModels, etc.) share
  // a single upstream pipeline instead of each running their own reduceState +
  // debounceTime. Without this, one action burst produces N modelCss$
  // emissions (one per subscriber), which cascades through downstream
  // combineLatests and triggers N redundant view rebuilds. See ARD-32076.
  //
  // `refCount: false` is intentional: the reduceState pipeline accumulates the
  // styles `Map` over the action stream, and we want it alive for the app's
  // lifetime. If the last subscriber unmounted and refCount tore down the
  // upstream, that accumulated state would be lost, and actions dispatched in
  // the gap would never be reduced into a future subscriber's snapshot.
  shareReplay({ bufferSize: 1, refCount: false })
);

export { modelCss$ };
